import { createStore, Provider } from 'jotai';
import type { ReactElement, ReactNode } from 'react';
import { useState } from 'react';
import type { DataRouter } from 'react-router-dom';

import { useAuth } from '@/auth/useAuth';
import { useCameraPreviewRefresh } from '@/hooks/useCameraPreviewRefresh';
import { useNotificationsBridge } from '@/hooks/useNotificationsBridge';
import { usePortalMonitorBridge } from '@/hooks/usePortalMonitorBridge';
import { useRouterBridge } from '@/hooks/useRouterBridge';
import { useServerUpdateGate } from '@/hooks/useServerUpdateGate';
import { useSessionMe } from '@/hooks/useSessionMe';
import { seedStore } from '@/state/seedStore';
import type { Store } from '@/types/store';

interface Props {
    router: DataRouter;
    children: ReactNode;
}

/** A store that already holds what must be readable before anything in it loads. */
function createSeededStore(router: DataRouter): Store {
    const store = createStore();
    seedStore(store, router);
    return store;
}

/**
 * Scopes every atom to one sign-in in one organization, feeds the notifications stream, the
 * portal monitor and the router's location into them for as long as the store lives, keeps the
 * cameras' previews current, grades what each media_server sends by the organization's update
 * reports, and keeps the signed-in person's /me read for the readers behind the gate. A switch or
 * a sign-out makes a new store and the key remounts the tree under it, so neither the next org
 * nor the next person can read the data. A signed-out page is its own scope, which is what the
 * empty key is.
 */
export const StoreProvider = ({ router, children }: Props): ReactElement => {
    const { isSignedIn, orgId } = useAuth();
    // A sign-out keeps the organization for the next sign-in, which can be another person's.
    const scopeId = isSignedIn ? orgId : null;
    // Held in state, which React keeps for the component's life: losing it empties every atom.
    const [scope, setScope] = useState(() => ({ scopeId, store: createSeededStore(router) }));
    let { store } = scope;
    if (scope.scopeId !== scopeId) {
        store = createSeededStore(router);
        setScope({ scopeId, store });
    }
    useNotificationsBridge(store);
    usePortalMonitorBridge(store);
    useRouterBridge(store, router);
    useCameraPreviewRefresh(store);
    useServerUpdateGate(store);
    useSessionMe(store);
    return (
        <Provider
            key={scopeId ?? ''}
            store={store}
        >
            {children}
        </Provider>
    );
};
